import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, saveSessionToken } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import { Mail, Lock, User, KeyRound, ShieldCheck, LogIn, UserPlus } from "lucide-react";

const detailToString = (d) => {
  if (!d) return "Errore";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || JSON.stringify(d);
};

// ---------------- Google (Emergent-managed) ----------------
function GoogleButton({ label }) {
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      onClick={handleGoogle}
      data-testid="google-signin-button"
      className="w-full bg-white border border-[#D8CFBB] text-[#1A1610] hover:bg-[#FAFAF8] rounded-full h-12 font-medium"
    >
      <svg className="me-2 h-5 w-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {label}
    </Button>
  );
}

// ---------------- Login (email + password) ----------------
function LoginForm({ onSuccess, switchToForgot, switchToRegister }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.session_token) saveSessionToken(data.session_token);
      toast.success("Bentornato/a");
      onSuccess(data.user);
    } catch (err) {
      toast.error(detailToString(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <div>
        <Label htmlFor="login-email" className="text-[#4A4A4A]">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
          <Input id="login-email" data-testid="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="login-password" className="text-[#4A4A4A]">Password</Label>
          <button type="button" onClick={switchToForgot} data-testid="link-forgot" className="text-xs text-[#B8965A] hover:underline">Password dimenticata?</button>
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
          <Input id="login-password" data-testid="login-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
        </div>
      </div>
      <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full h-11">
        <LogIn className="me-2 h-4 w-4" />
        {busy ? "…" : "Accedi"}
      </Button>
      <p className="text-xs text-center text-[#6B5F4C]">
        Non hai un account?{" "}
        <button type="button" onClick={switchToRegister} data-testid="link-register" className="text-[#B8965A] hover:underline">Registrati</button>
      </p>
    </form>
  );
}

// ---------------- Register + email verify ----------------
function RegisterForm({ onRegistered }) {
  const [step, setStep] = useState("form"); // form | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("buyer");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name, role });
      if (data.requires_verification) {
        toast.success("Ti abbiamo inviato un codice via email");
        setStep("verify");
      } else {
        toast.success(data.message || "Registrazione completata");
        onRegistered(null); // redirect to login
      }
    } catch (err) {
      toast.error(detailToString(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, code });
      if (data?.session_token) saveSessionToken(data.session_token);
      toast.success("Email verificata");
      onRegistered(data.user);
    } catch (err) {
      toast.error(detailToString(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Nuovo codice inviato");
    } catch (err) {
      toast.error(detailToString(err?.response?.data?.detail));
    }
  };

  if (step === "verify") {
    return (
      <form onSubmit={verify} className="space-y-4" data-testid="verify-email-form">
        <div className="text-center">
          <ShieldCheck className="h-10 w-10 text-[#B8965A] mx-auto mb-2" />
          <h4 className="text-lg font-medium">Verifica email</h4>
          <p className="text-xs text-[#6B5F4C]">Abbiamo inviato un codice a<br/><span className="font-mono">{email}</span></p>
        </div>
        <div>
          <Label htmlFor="verify-code" className="text-[#4A4A4A]">Codice OTP (6 cifre)</Label>
          <Input id="verify-code" data-testid="verify-code" required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-center text-2xl tracking-[0.5em] font-mono border-[#D8CFBB] bg-white" />
        </div>
        <Button type="submit" disabled={busy || code.length !== 6} data-testid="verify-submit" className="w-full bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full h-11">
          {busy ? "…" : "Verifica e accedi"}
        </Button>
        <div className="flex justify-between text-xs">
          <button type="button" onClick={() => setStep("form")} className="text-[#6B5F4C] hover:underline">Cambia email</button>
          <button type="button" onClick={resend} data-testid="verify-resend" className="text-[#B8965A] hover:underline">Rinvia codice</button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <div>
        <Label htmlFor="reg-name" className="text-[#4A4A4A]">Nome</Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
          <Input id="reg-name" data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
        </div>
      </div>
      <div>
        <Label htmlFor="reg-email" className="text-[#4A4A4A]">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
          <Input id="reg-email" data-testid="register-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
        </div>
      </div>
      <div>
        <Label htmlFor="reg-password" className="text-[#4A4A4A]">Password (min 8 caratteri, lettere + numeri)</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
          <Input id="reg-password" data-testid="register-password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
        </div>
      </div>
      <div>
        <Label htmlFor="reg-role" className="text-[#4A4A4A]">Sono un/una</Label>
        <select id="reg-role" data-testid="register-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-10 rounded-md border border-[#D8CFBB] bg-white px-3 text-sm">
          <option value="buyer">Acquirente</option>
          <option value="agent">Agente immobiliare</option>
          <option value="owner">Proprietario</option>
        </select>
      </div>
      <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full h-11">
        <UserPlus className="me-2 h-4 w-4" />
        {busy ? "…" : "Crea account"}
      </Button>
    </form>
  );
}

// ---------------- Forgot password ----------------
function ForgotForm({ onDone }) {
  const [step, setStep] = useState("request"); // request | reset
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Se l'email esiste, riceverai un codice");
      setStep("reset");
    } catch (err) {
      toast.error(detailToString(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPass });
      toast.success("Password aggiornata");
      onDone();
    } catch (err) {
      toast.error(detailToString(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (step === "request") {
    return (
      <form onSubmit={request} className="space-y-4" data-testid="forgot-form">
        <div>
          <Label htmlFor="fp-email" className="text-[#4A4A4A]">La tua email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
            <Input id="fp-email" data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full h-11">
          {busy ? "…" : "Invia codice"}
        </Button>
        <p className="text-xs text-center">
          <button type="button" onClick={onDone} className="text-[#6B5F4C] hover:underline">Torna al login</button>
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={reset} className="space-y-4" data-testid="reset-form">
      <p className="text-xs text-[#6B5F4C] text-center">Se l'email esiste, hai ricevuto un codice.<br/>Inseriscilo insieme alla nuova password.</p>
      <div>
        <Label htmlFor="rst-code" className="text-[#4A4A4A]">Codice OTP</Label>
        <Input id="rst-code" data-testid="reset-code" required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-center font-mono tracking-[0.4em] border-[#D8CFBB] bg-white" />
      </div>
      <div>
        <Label htmlFor="rst-newpass" className="text-[#4A4A4A]">Nuova password</Label>
        <div className="relative">
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A8B72]" />
          <Input id="rst-newpass" data-testid="reset-newpass" type="password" required minLength={8} value={newPass} onChange={(e) => setNewPass(e.target.value)} className="ps-9 border-[#D8CFBB] bg-white" />
        </div>
      </div>
      <Button type="submit" disabled={busy || code.length !== 6} data-testid="reset-submit" className="w-full bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full h-11">
        {busy ? "…" : "Reimposta password e accedi"}
      </Button>
    </form>
  );
}

// ---------------- Main Login page ----------------
export default function Login() {
  const navigate = useNavigate();
  const { lang, setUser } = useApp();
  const [mode, setMode] = useState("login"); // login | register | forgot

  // Reset the mode display when the user navigates away or lang changes shouldn't reset
  useEffect(() => { /* no-op — kept to declare intent */ }, []);

  const goDashboard = (user) => {
    if (user) setUser(user);
    navigate("/dashboard", { replace: true });
  };

  const activeForm = (() => {
    if (mode === "register") return <RegisterForm onRegistered={goDashboard} />;
    if (mode === "forgot") return <ForgotForm onDone={() => setMode("login")} />;
    return <LoginForm onSuccess={goDashboard} switchToForgot={() => setMode("forgot")} switchToRegister={() => setMode("register")} />;
  })();

  return (
    <div className="min-h-screen bg-[#F5F0E6] text-[#1A1610]">
      <Header />
      <div className="mx-auto max-w-md px-6 py-16">
        {(() => {
          let title, sub;
          if (mode === "register") {
            title = "Registrati";
            sub = "Crea un account per accedere alla bacheca off-market e firmare i mandati.";
          } else if (mode === "forgot") {
            title = "Recupera password";
            sub = "Inserisci la tua email: se registrata, riceverai un codice per reimpostare la password.";
          } else {
            title = t("cta_signin", lang);
            sub = t("login_hint", lang);
          }
          return (
            <>
              <p className="text-xs tracking-[0.4em] uppercase text-[#B8965A] mb-3">Members access</p>
              <h1 className="text-4xl sm:text-5xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {title}
              </h1>
              <p className="text-[#6B5F4C] mb-8 leading-relaxed text-sm">{sub}</p>
            </>
          );
        })()}

        {mode !== "forgot" && (
          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="bg-[#EDE4CF] border border-[#D8CFBB] rounded-full p-1 w-full grid grid-cols-2">
              <TabsTrigger value="login" data-testid="tab-login" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">Registrati</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div className="rounded-lg border border-[#D8CFBB] bg-white p-8 space-y-5">
          {activeForm}

          {mode !== "forgot" && (
            <>
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E5E5E3]" /></div>
                <div className="relative flex justify-center text-xs uppercase tracking-widest text-[#9A8B72]">
                  <span className="bg-white px-3">oppure</span>
                </div>
              </div>
              <div className="space-y-3">
                <GoogleButton label={t("login_continue_google", lang)} />
              </div>
            </>
          )}

          <p className="mt-4 text-xs text-[#9A8B72] text-center leading-relaxed">
            {t("login_terms", lang)}
          </p>
        </div>
      </div>
    </div>
  );
}
